import { FC, useEffect } from "react";
import { useFragment, useRelayEnvironment } from "react-relay";
import { graphql, requestSubscription } from "relay-runtime";
import {
  AssetLabel,
  BreakPoints,
  Decimal,
  Fiat,
  i18n,
  IconDenom,
  Icons,
  nFormatter,
  useWindowSize,
} from "rujira.ui";
import { StarSolid, TrendDown } from "rujira.ui/src/components/icons/Icons";
import { useFavorites } from "../../services/favorites";
import { HeaderTradeFragment$key } from "./__generated__/HeaderTradeFragment.graphql";
import { HeaderTradeSummaryFragment$key } from "./__generated__/HeaderTradeSummaryFragment.graphql";
const { AngleDown, Star, TrendUp } = Icons;

const fragment = graphql`
  fragment HeaderTradeFragment on FinPair {
    id
    address
    assetBase {
      asset
      type
      chain
      metadata {
        symbol
        decimals
      }
      price {
        mcap
      }
    }
    assetQuote {
      asset
      type
      chain
      metadata {
        symbol
        decimals
      }
    }
    summary {
      ...HeaderTradeSummaryFragment
    }
  }
`;

const summaryFragment = graphql`
  fragment HeaderTradeSummaryFragment on FinSummary {
    last
    lastUsd
    high
    low
    change
    volumeUsd
  }
`;

const subscription = graphql`
  subscription HeaderTradeSubscription($id: ID!) {
    node(id: $id) {
      ... on FinPair {
        summary {
          ...HeaderTradeSummaryFragment
        }
      }
    }
  }
`;

export const Header: FC<{
  k?: HeaderTradeFragment$key;
  openDrawer: () => void;
}> = ({ k, openDrawer }) => {
  const data = useFragment(fragment, k);
  const summary = useFragment<HeaderTradeSummaryFragment$key>(
    summaryFragment,
    data?.summary
  );

  const { favorites, addFavorite, removeFavorite } = useFavorites();

  const { width } = useWindowSize();
  const min = 470;

  const decimals = 4; // get from base denom
  const change = BigInt(summary?.change || 0);

  const starClick = () => {
    if (!data?.id) return;
    if (favorites.includes(data?.id)) {
      removeFavorite(data?.id);
    } else {
      addFavorite(data?.id);
    }
  };

  const env = useRelayEnvironment();

  useEffect(() => {
    if (!data?.id) return;
    const sub = requestSubscription(env, {
      subscription,
      variables: {
        id: data?.id,
      },
    });
    return () => {
      sub.dispose();
    };
  }, [data?.id]);

  return (
    <>
      <button
        className="trade__header-pair"
        onClick={() => {
          openDrawer();
        }}>
        <IconDenom
          denom={data?.assetBase.metadata.symbol || ""}
          className="denom"
        />
        <IconDenom
          denom={data?.assetQuote.metadata.symbol || ""}
          className="denom"
        />
        <h1 className="mr-1">
          <AssetLabel
            asset={data?.assetBase}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
          <span>/</span>{" "}
          <AssetLabel
            asset={data?.assetQuote}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
        </h1>
        <AngleDown />
      </button>
      <div className="trade__header-price">
        <Decimal
          amount={BigInt(summary?.last || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
          as="h2"
        />
        <Fiat
          amount={BigInt(summary?.lastUsd || 0)}
          decimals={12}
          symbol="$"
          padSymbol={false}
        />
      </div>
      <div className="trade__header-stat">
        <h4>24h Change</h4>
        {change > 0n ? (
          <div className="flex ai-c color-teal">
            <span>+{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
            <TrendUp className="block w-2 ml-0.5" />
          </div>
        ) : change < 0n ? (
          <div className="flex ai-c color-red">
            <span>{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
            <TrendDown className="block w-2 ml-0.5" />
          </div>
        ) : (
          <div className="flex ai-c color-teal">
            <span>0.00%</span>
          </div>
        )}
      </div>
      <div className="trade__header-stat">
        <h4>24h High</h4>
        <Decimal
          subscript
          amount={BigInt(summary?.high || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
          symbol={data?.assetQuote.metadata.symbol}
          symbolClassName={"fs-12 color-grey condensed fw-500"}
        />
      </div>
      <div className="trade__header-stat">
        <h4>24h Low</h4>
        <Decimal
          subscript
          amount={BigInt(summary?.low || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
          symbol={data?.assetQuote.metadata.symbol}
          symbolClassName={"fs-12 color-grey condensed fw-500"}
        />
      </div>
      <div className="trade__header-stat">
        <h4>24h Vol</h4>
        <div>
          ${nFormatter(BigInt(summary?.volumeUsd || 0), width < min ? 2 : 3, 8)}
        </div>
      </div>
      {data?.assetBase.price?.mcap && width > BreakPoints.small ? (
        <div className="trade__header-stat">
          <h4>Market Cap</h4>
          <div>
            $
            {nFormatter(
              BigInt(data?.assetBase.price?.mcap || 0),
              width < min ? 2 : 3,
              0
            )}
          </div>
        </div>
      ) : null}
      {data?.id && favorites.includes(data?.id) ? (
        <StarSolid
          className="trade__header-favorite trade__header-favorite--solid pointer"
          onClick={starClick}
        />
      ) : (
        <Star className="trade__header-favorite pointer" onClick={starClick} />
      )}
      <div className="break" />
    </>
  );
};

export const MarketSelect: FC<{
  k?: HeaderTradeFragment$key;
  openDrawer: () => void;
}> = ({ k, openDrawer }) => {
  const data = useFragment(fragment, k);
  const { favorites, addFavorite, removeFavorite } = useFavorites();

  const starClick = () => {
    if (!data?.id) return;
    if (favorites.includes(data?.id)) {
      removeFavorite(data?.id);
    } else {
      addFavorite(data?.id);
    }
  };

  return (
    <div className="flex condensed ai-c">
      <button
        className="trade__header-pair"
        onClick={() => {
          openDrawer();
        }}>
        <IconDenom
          denom={data?.assetBase.metadata.symbol || ""}
          className="denom"
        />
        <IconDenom
          denom={data?.assetQuote.metadata.symbol || ""}
          className="denom"
        />
        <h1 className="mr-1">
          <AssetLabel
            asset={data?.assetBase}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
          <span>/</span>{" "}
          <AssetLabel
            asset={data?.assetQuote}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
        </h1>
        <AngleDown />
      </button>
      {data?.id && favorites.includes(data?.id) ? (
        <StarSolid
          className="trade__header-favorite trade__header-favorite--solid pointer"
          onClick={starClick}
        />
      ) : (
        <Star className="trade__header-favorite pointer" onClick={starClick} />
      )}
    </div>
  );
};

export const MarketSummary: FC<{
  k?: HeaderTradeFragment$key;
}> = ({ k }) => {
  const data = useFragment(fragment, k);
  const summary = useFragment<HeaderTradeSummaryFragment$key>(
    summaryFragment,
    data?.summary
  );

  const { width } = useWindowSize();
  const min = 470;

  const decimals = 4; // get from base denom
  const change = BigInt(summary?.change || 0);

  const env = useRelayEnvironment();

  useEffect(() => {
    if (!data?.id) return;
    const sub = requestSubscription(env, {
      subscription,
      variables: {
        id: data?.id,
      },
    });
    return () => {
      sub.dispose();
    };
  }, [data?.id]);

  return (
    <div className="trade__header">
      <div className="trade__header-price">
        <Decimal
          amount={BigInt(summary?.last || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
          as="h2"
        />
        <Fiat
          amount={BigInt(summary?.lastUsd || 0)}
          decimals={12}
          symbol="$"
          padSymbol={false}
        />
      </div>
      <div className="trade__header-stat">
        <h4>24h Change</h4>
        {change > 0n ? (
          <div className="flex ai-c color-teal">
            <span>+{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
            <TrendUp className="block w-2 ml-0.5" />
          </div>
        ) : change < 0n ? (
          <div className="flex ai-c color-red">
            <span>{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
            <TrendDown className="block w-2 ml-0.5" />
          </div>
        ) : (
          <div className="flex ai-c color-teal">
            <span>0.00%</span>
          </div>
        )}
      </div>
      <div className="trade__header-stat">
        <h4>24h High</h4>
        <Decimal
          amount={BigInt(summary?.high || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
        />
      </div>
      <div className="trade__header-stat">
        <h4>24h Low</h4>
        <Decimal
          amount={BigInt(summary?.low || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
        />
      </div>
      <div className="trade__header-stat">
        <h4>24h Vol</h4>
        <div>
          ${nFormatter(BigInt(summary?.volumeUsd || 0), width < min ? 2 : 3, 8)}
        </div>
      </div>
      {data?.assetBase.price?.mcap ? (
        <div className="trade__header-stat">
          <h4>Market Cap</h4>
          <div>
            $
            {nFormatter(
              BigInt(data?.assetBase.price?.mcap || 0),
              width < min ? 2 : 3,
              0
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const MarketSelectFallback = () => (
  <div className="flex condensed ai-c">
    <div className="trade__header-pair">
      <div className="skeleton denom br-4" />
      <div className="skeleton denom br-4" />
      <i18n.h1 className="color-grey mr-4">Loading...</i18n.h1>
    </div>
  </div>
);

export const MarketSummaryFallback = () => (
  <>
    <div className="trade__header-pair">
      <div className="skeleton denom br-4" />
      <div className="skeleton denom br-4" />
      <i18n.h1 className="color-grey mr-4">Loading...</i18n.h1>
    </div>
    <div className="trade__header-price">
      <i18n.h2>Loading...</i18n.h2>
      <Fiat amount={0n} symbol="$" padSymbol={false} />
    </div>
    <div className="trade__header-stat">
      <i18n.h4>24h Change</i18n.h4>
      <span>Loading...</span>
    </div>
    <div className="trade__header-stat">
      <i18n.h4>24h High</i18n.h4>
      <span>Loading...</span>
    </div>
    <div className="trade__header-stat">
      <i18n.h4>24h Low</i18n.h4>
      <span>Loading...</span>
    </div>
    <div className="trade__header-stat">
      <i18n.h4>24h Vol</i18n.h4>
      <span>Loading...</span>
    </div>
    <div className="break" />
  </>
);
